import { Service, service, inject } from './di'
import { ConfigService } from './ConfigService'
import { Country, LocationService } from './LocationService'
import { DateTimeService } from './DateTimeService'
import { ReleaseService } from './ReleaseService'

export const GiveawayPeriod = {
    BLACK_FRIDAY: 'BLACK_FRIDAY',
    CHRISTMAS: 'CHRISTMAS',
    BACK_TO_SCHOOL: 'BACK_TO_SCHOOL',
}

@service('BlackFridayService')
export class BlackFridayService extends Service {
    @inject config: ConfigService
    @inject location: LocationService
    @inject dateService: DateTimeService
    @inject releaseService: ReleaseService

    timeZone = 'America/New_York'

    isEnabled() {
        return this.releaseService.shouldUseBlackfridayDesign
    }

    isOnHost() {
        return (
            location.href.includes('/black-friday') ||
            location.href.includes('/vendredi-fou')
        )
    }

    // checking if the Javascript is being run on Rendora
    // is_bf is being sent on nginx server
    // location:4000 - is Rendora host
    isOnRendora() {
        return (
            location.host === 'localhost:4000' &&
            location.search.includes('is_bf=1')
        )
    }

    getHostLink() {
        const path =
            this.location.locale === 'fr-ca'
                ? 'circulaires'
                : this.location.locale === 'en-us'
                  ? 'weekly_ads'
                  : 'flyers'
        // const category = 'cyber-week'
        const category = this.config.BF_CATEGORY
        return `${this.location.locale}/${path}/${category}?postal_code=${this.location.postalCode}`
    }

    redirectToHost() {
        window.location.href = this.getHostLink()
    }

    getGiveawayLink() {
        const dateNow = this.dateService.fromJsDate(new Date(), this.timeZone)
        const day = dateNow.day
        const month = dateNow.month
        const locale = this.location.locale
        const linksForDay = GIVEAWAY_LINKS[`${day}-${month}`]
        return linksForDay ? linksForDay[locale] : '/'
    }

    isBlackFridayGiveawayPeriod(): boolean {
        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.has('giveaway')) return true

        const dateNow = this.getNow()
        const dateStart = this.dateService.fromJsDate(
            new Date('November 18, 2024 00:00:01'),
            this.timeZone
        )
        const dateEnd = this.dateService.fromJsDate(
            new Date('December 6, 2024 23:59:59'),
            this.timeZone
        )
        return dateNow >= dateStart && dateNow <= dateEnd
    }

    isChristmasGiveawayPeriod(): boolean {
        const dateNow = this.getNow()
        const dateStart = this.dateService.fromJsDate(
            new Date('December 1, 2023 00:00:01'),
            this.timeZone
        )
        const dateEnd = this.dateService.fromJsDate(
            new Date('December 24, 2023 23:59:59'),
            this.timeZone
        )
        return dateNow >= dateStart && dateNow <= dateEnd
    }

    isBackToSchoolGiveawayPeriod(): boolean {
        const dateNow = this.getNow()
        const dateStart = this.dateService.fromJsDate(
            new Date('August 21, 2024 00:00:01'),
            this.timeZone
        )
        const dateEnd = this.dateService.fromJsDate(
            new Date('August 24, 2024 23:59:59'),
            this.timeZone
        )
        return dateNow >= dateStart && dateNow <= dateEnd
    }

    isProudlyCanadianPeriod(): boolean {
        const dateNow = this.getNow()

        const dateEnd = this.dateService.fromJsDate(
            new Date('May 31, 2025 23:59:59'),
            this.timeZone
        )
        return dateNow <= dateEnd
    }

    getGiveawayPeriod(): string {
        if (this.isBlackFridayGiveawayPeriod())
            return GiveawayPeriod.BLACK_FRIDAY
        if (
            this.isChristmasGiveawayPeriod() &&
            this.location.country === Country.CANADA
        )
            return GiveawayPeriod.CHRISTMAS
        if (this.isBackToSchoolGiveawayPeriod())
            return GiveawayPeriod.BACK_TO_SCHOOL
        return null
    }

    getNow() {
        return this.dateService.fromJsDate(new Date(), this.timeZone)
    }

    isCyberWeekPeriod() {
        // No Cyber Week in 2024
        return false
        // const dateNow = this.dateService.fromJsDate(new Date(), this.timeZone)
        // const dateStart = this.dateService.fromJsDate(
        //     new Date('November 25, 2023 00:00:01'),
        //     this.timeZone
        // )
        // return dateNow >= dateStart
    }
}

const GIVEAWAY_LINKS: any = {
    '18-11': {
        'en-ca': 'https://gleam.io/AfcAn/canada-black-friday-giveaway-day-1',
        'fr-ca': 'https://gleam.io/HNtwd/concours-vendredi-fou-jour-1',
        'en-us': 'https://gleam.io/lLw7K/us-black-friday-giveaway-day-1',
    },
    '19-11': {
        'en-ca': 'https://gleam.io/kKT6u/canada-black-friday-giveaway-day-2',
        'fr-ca': 'https://gleam.io/V1IDr/concours-vendredi-fou-jour-2',
        'en-us': 'https://gleam.io/wsqJk/us-black-friday-giveaway-day-2',
    },
    '20-11': {
        'en-ca': 'https://gleam.io/9J6BS/canada-black-friday-giveaway-day-3',
        'fr-ca': 'https://gleam.io/Jwfqi/concours-vendredi-fou-jour-3',
        'en-us': 'https://gleam.io/PsTDn/us-black-friday-giveaway-day-3',
    },
    '21-11': {
        'en-ca': 'https://gleam.io/r8ocY/canada-black-friday-giveaway-day-4',
        'fr-ca': 'https://gleam.io/bGrrC/concours-vendredi-fou-jour-4',
        'en-us': 'https://gleam.io/frKQ4/us-black-friday-giveaway-day-4',
    },
    '22-11': {
        'en-ca': 'https://gleam.io/5dbTK/canada-black-friday-giveaway-day-5',
        'fr-ca': 'https://gleam.io/WGZr2/concours-vendredi-fou-jour-5',
        'en-us': 'https://gleam.io/7nDq4/us-black-friday-giveaway-day-5',
    },
    '23-11': {
        'en-ca': 'https://gleam.io/cXqbr/canada-black-friday-giveaway-day-6',
        'fr-ca': 'https://gleam.io/uwjaS/concours-vendredi-fou-jour-6',
        'en-us': 'https://gleam.io/Eiu6C/us-black-friday-giveaway-day-6',
    },
    '24-11': {
        'en-ca': 'https://gleam.io/mGeNU/canada-black-friday-giveaway-day-7',
        'fr-ca': 'https://gleam.io/FsEjw/concours-vendredi-fou-jour-7',
        'en-us': 'https://gleam.io/iKsMe/us-black-friday-giveaway-day-7',
    },
    '25-11': {
        'en-ca': 'https://gleam.io/dEnXr/canada-black-friday-giveaway-day-8',
        'fr-ca': 'https://gleam.io/rrNOZ/concours-vendredi-fou-jour-8',
        'en-us': 'https://gleam.io/w3X6Z/us-black-friday-giveaway-day-8',
    },
    '26-11': {
        'en-ca': 'https://gleam.io/vWhLx/canada-black-friday-giveaway-day-9',
        'fr-ca': 'https://gleam.io/BY8zW/concours-vendredi-fou-jour-9',
        'en-us': 'https://gleam.io/JYQ9u/us-black-friday-giveaway-day-9',
    },
    '27-11': {
        'en-ca': 'https://gleam.io/G35nx/canada-black-friday-giveaway-day-10',
        'fr-ca': 'https://gleam.io/qROpM/concours-vendredi-fou-jour-10',
        'en-us': 'https://gleam.io/EXRHY/us-black-friday-giveaway-day-10',
    },
    '28-11': {
        'en-ca': 'https://gleam.io/lW5uy/canada-black-friday-giveaway-day-11',
        'fr-ca': 'https://gleam.io/N2uZq/concours-vendredi-fou-jour-11',
        'en-us': 'https://gleam.io/mpsxV/us-black-friday-giveaway-day-11',
    },
    '29-11': {
        'en-ca': 'https://gleam.io/dhNbG/canada-black-friday-giveaway-day-12',
        'fr-ca': 'https://gleam.io/sh3hW/concours-vendredi-fou-jour-12',
        'en-us': 'https://gleam.io/iuuvm/us-black-friday-giveaway-day-12',
    },
    '30-11': {
        'en-ca': 'https://gleam.io/23auU/canada-black-friday-giveaway-day-13',
        'fr-ca': 'https://gleam.io/GRgYU/concours-vendredi-fou-jour-13',
        'en-us': 'https://gleam.io/Hx9Rq/us-black-friday-giveaway-day-13',
    },
    '1-12': {
        'en-ca': 'https://gleam.io/ojoz2/canada-black-friday-giveaway-day-14',
        'fr-ca': 'https://gleam.io/dsI3b/concours-vendredi-fou-jour-14',
        'en-us': 'https://gleam.io/embxy/us-black-friday-giveaway-day-14',
    },
    '2-12': {
        'en-ca': 'https://gleam.io/sB2t5/canada-black-friday-giveaway-day-15',
        'fr-ca': 'https://gleam.io/OOuZ9/concours-de-vendredi-fou-jour-15',
        'en-us': 'https://gleam.io/wlvaH/us-black-friday-giveaway-day-15',
    },
    '3-12': {
        'en-ca': 'https://gleam.io/HpTj1/canada-black-friday-giveaway-day-16',
        'fr-ca': 'https://gleam.io/gEKAP/concours-de-vendredi-fou-jour-16',
        'en-us': 'https://gleam.io/EOUtO/us-black-friday-giveaway-day-16',
    },
    '4-12': {
        'en-ca': 'https://gleam.io/kFIA5/canada-black-friday-giveaway-day-17',
        'fr-ca': 'https://gleam.io/hT0wd/concours-de-vendredi-fou-jour-17',
        'en-us': 'https://gleam.io/UFdgw/us-black-friday-giveaway-day-17',
    },
    '5-12': {
        'en-ca': 'https://gleam.io/M4U8P/canada-black-friday-giveaway-day-18',
        'fr-ca': 'https://gleam.io/swGe6/concours-de-vendredi-fou-jour-18',
        'en-us': 'https://gleam.io/kslN3/us-black-friday-giveaway-day-18',
    },
    '6-12': {
        'en-ca': 'https://gleam.io/DzIzE/canada-black-friday-giveaway-day-19',
        'fr-ca': 'https://gleam.io/oB9Uc/concours-de-vendredi-fou-jour-19',
        'en-us': 'https://gleam.io/qEfR0/us-black-friday-giveaway-day-19',
    },
}
